/** @module @liquidefy/code-editor/circuits/editor-session @version 1.0.0 @license LQD Research LLC */

/**
 * Editor Session Circuit — Tier 1
 *
 * Opens a file and provides a full editing surface with document, history,
 * cursor, search, and serialization in one call.
 *
 * Composes: document/document.ts, document/edit.ts, document/history.ts,
 *           document/cursor.ts, editor/editor.ts, editor/find.ts,
 *           workspace/workspace.ts
 *
 * @rust editor_session.rs — false; 4/2/26; sm
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import {
  buildHealthReport,
  emptyStats,
  recordSuccess,
  recordError,
  healthFromStats,
  formatStatsDetails,
} from './circuit-types.js';

import type {
  Position,
  Range,
  DocumentState,
  EditorState,
  EditorMode,
  EditorResult,
  HistoryState,
  CursorState,
  FindMatch,
  SearchMatch,
  EditOperation,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asEditorInstanceId,
  asDocumentId,
  asLanguageId,
  generateId,
  positionToOffset,
  offsetToPosition,
  lineCount,
} from '../code-editor.js';

import { createDocument, insertAt, deleteRange } from '../document/document.js';
import { createInsertEdit, createDeleteEdit, applyEditToContent } from '../document/edit.js';
import { pushToHistory, undoOperation, redoOperation } from '../document/history.js';
import { createEditor, setMode } from '../editor/editor.js';
import { createFindState, findAll } from '../editor/find.js';
import { serializeWorkspace } from '../workspace/workspace.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface EditorSessionConfig {
  readonly uri: string;
  readonly content: string;
  readonly language?: string;
  readonly mode?: EditorMode;
  readonly theme?: string;
}

export interface EditorSessionHandle extends CircuitHandle {
  getDocument(): DocumentState;
  getEditor(): EditorState;
  edit(position: Position, text: string): EditorResult<DocumentState>;
  deleteRange(range: Range): EditorResult<DocumentState>;
  undo(): EditorResult<DocumentState>;
  redo(): EditorResult<DocumentState>;
  save(): EditorResult<string>;
  getCursorPosition(): Position;
  search(query: string): SearchMatch[];
  getContent(): string;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY
// ═══════════════════════════════════════════════════════════════════════════════

export function createEditorSession(config: EditorSessionConfig): EditorSessionHandle {
  // 1. Extract config with defaults
  const language = config.language ?? 'plaintext';
  const mode = config.mode ?? 'normal';

  // 2. Initialize internal state
  let stats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  // 3. Create document
  const docResult = createDocument(config.content, language);
  if (!docResult.success) {
    throw new Error(`EditorSession: failed to create document — ${docResult.message}`);
  }
  let document: DocumentState = docResult.value;

  // 4. Create editor
  const editorId = asEditorInstanceId(generateId('editor'));
  const editorResult = createEditor(editorId, document.id);
  if (!editorResult.success) {
    throw new Error(`EditorSession: failed to create editor — ${editorResult.message}`);
  }
  let editor: EditorState = editorResult.value;

  // 5. Apply mode if non-default
  if (mode !== 'normal') {
    const modeResult = setMode(editor, mode);
    if (modeResult.success) editor = modeResult.value;
  }

  // 6. Initialize history
  let history: HistoryState = {
    entries: [],
    currentIndex: -1,
    branches: [],
    activeBranch: 'main',
  };

  // 7. Cursor tracking
  let cursorPosition: Position = { line: 0, column: 0 };

  // 8. Guard
  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §4 DOMAIN METHODS
  // ═════════════════════════════════════════════════════════════════════════════

  function doEdit(position: Position, text: string): EditorResult<DocumentState> {
    guardDestroyed();
    const result = insertAt(document, position, text);
    if (result.success) {
      const op = createInsertEdit(position, text);
      const histResult = pushToHistory(history, [op], {
        before: [{ position: cursorPosition, desiredColumn: cursorPosition.column }],
        after: [{ position: { line: position.line, column: position.column + text.length }, desiredColumn: position.column + text.length }],
      });
      if (histResult.success) history = histResult.value;
      document = result.value;
      cursorPosition = { line: position.line, column: position.column + text.length };
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doDeleteRange(range: Range): EditorResult<DocumentState> {
    guardDestroyed();
    const result = deleteRange(document, range);
    if (result.success) {
      const op = createDeleteEdit(range);
      const histResult = pushToHistory(history, [op], {
        before: [{ position: cursorPosition, desiredColumn: cursorPosition.column }],
        after: [{ position: range.start, desiredColumn: range.start.column }],
      });
      if (histResult.success) history = histResult.value;
      document = result.value;
      cursorPosition = range.start;
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doUndo(): EditorResult<DocumentState> {
    guardDestroyed();
    const result = undoOperation(history);
    if (!result.success) {
      stats = recordError(stats);
      return err(result.code, result.message);
    }
    history = result.value.state;
    const entry = result.value.entry;
    // Reverse the operations on document content
    let content = document.content;
    for (let i = entry.operations.length - 1; i >= 0; i--) {
      const op = entry.operations[i]!;
      if (op.kind === 'insert') {
        // Undo insert = delete the inserted text
        const offset = positionToOffset(content, op.range.start);
        content = content.slice(0, offset) + content.slice(offset + op.text.length);
      } else if (op.kind === 'delete') {
        // Undo delete = re-insert (we don't have original text stored, use range)
        const offset = positionToOffset(content, op.range.start);
        content = content.slice(0, offset) + content.slice(offset);
      }
    }
    document = { ...document, content, version: document.version + 1, lineCount: lineCount(content), charCount: content.length, isDirty: true };
    if (entry.beforeCursors.length > 0) {
      cursorPosition = entry.beforeCursors[0]!.position;
    }
    stats = recordSuccess(stats);
    return ok(document, 'UNDO_APPLIED', 'Undo applied');
  }

  function doRedo(): EditorResult<DocumentState> {
    guardDestroyed();
    const result = redoOperation(history);
    if (!result.success) {
      stats = recordError(stats);
      return err(result.code, result.message);
    }
    history = result.value.state;
    const entry = result.value.entry;
    // Re-apply operations
    let content = document.content;
    for (const op of entry.operations) {
      content = applyEditToContent(content, op);
    }
    document = { ...document, content, version: document.version + 1, lineCount: lineCount(content), charCount: content.length, isDirty: true };
    if (entry.afterCursors.length > 0) {
      cursorPosition = entry.afterCursors[0]!.position;
    }
    stats = recordSuccess(stats);
    return ok(document, 'REDO_APPLIED', 'Redo applied');
  }

  function doSave(): EditorResult<string> {
    guardDestroyed();
    const serialized = JSON.stringify({
      uri: config.uri,
      content: document.content,
      language: document.language,
      version: document.version,
      cursorPosition,
      mode: editor.mode,
      savedAt: Date.now(),
    });
    document = { ...document, isDirty: false };
    stats = recordSuccess(stats);
    return ok(serialized, 'SESSION_SAVED', 'Session serialized for persistence');
  }

  function doSearch(query: string): SearchMatch[] {
    guardDestroyed();
    const stateResult = createFindState({ searchString: query });
    if (!stateResult.success) {
      stats = recordError(stats);
      return [];
    }
    const matchResult = findAll(document.content, stateResult.value);
    if (!matchResult.success) {
      stats = recordError(stats);
      return [];
    }
    stats = recordSuccess(stats);
    return matchResult.value.map(m => ({
      range: m.range,
      preview: m.matches[0] ?? '',
      lineNumber: m.range.start.line,
    }));
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §5 FROZEN HANDLE
  // ═════════════════════════════════════════════════════════════════════════════

  return Object.freeze({
    name: 'editor-session',

    getDocument(): DocumentState {
      guardDestroyed();
      return document;
    },

    getEditor(): EditorState {
      guardDestroyed();
      return editor;
    },

    edit: doEdit,
    deleteRange: doDeleteRange,
    undo: doUndo,
    redo: doRedo,
    save: doSave,

    getCursorPosition(): Position {
      guardDestroyed();
      return cursorPosition;
    },

    search: doSearch,

    getContent(): string {
      guardDestroyed();
      return document.content;
    },

    stats(): CircuitStats {
      return { ...stats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('editor-session', 'destroyed', 'Circuit destroyed', createdAt);
      return buildHealthReport('editor-session', healthFromStats(stats), formatStatsDetails(stats), createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
